import React from 'react';
import { Link } from 'react-router-dom';
import { Handshake, TrendingUp, Globe, Shield, ArrowRight, CheckCircle, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { downloadResources } from '../data/mock';

const Partner = () => {
  const benefits = [
    {
      icon: TrendingUp,
      title: 'Competitive Pricing',
      description: 'Direct sourcing from farms enables competitive pricing for bulk orders'
    },
    {
      icon: Shield,
      title: 'Quality Assurance',
      description: 'Multi-stage quality control and compliance with international standards'
    },
    {
      icon: Globe,
      title: 'Reliable Supply',
      description: 'Consistent product availability and predictable delivery schedules'
    },
    {
      icon: Handshake,
      title: 'Long-Term Partnership',
      description: 'Building sustainable relationships with transparent communication'
    }
  ];

  const partnerTypes = [
    {
      title: 'UK Retailers & Wholesalers',
      description: 'Supply African food products to your retail stores or wholesale distribution network',
      benefits: [
        'Regular container shipments',
        'Flexible order quantities',
        'Product variety options',
        'Quality consistency'
      ]
    },
    {
      title: 'International Importers',
      description: 'Expand your African produce portfolio with our reliable export services',
      benefits: [
        'Multiple product lines',
        'Full export documentation',
        'Customs support',
        'Market insights'
      ]
    },
    {
      title: 'Supermarkets & Food Services',
      description: 'Stock authentic African agricultural products for your diverse customer base',
      benefits: [
        'Fresh product supply',
        'Branded or unbranded options',
        'Consistent quality',
        'Promotional support'
      ]
    },
    {
      title: 'Distributors',
      description: 'Add premium African produce to your distribution portfolio',
      benefits: [
        'Bulk pricing advantages',
        'Mixed container options',
        'Logistics coordination',
        'Partnership growth'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxwYXJ0bmVyc2hpcHxlbnwwfHx8fDE3NzEyODAxNzh8MA&ixlib=rb-4.1.0&q=85"
            alt="Partner With Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1B5E20]/80"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Partner With Us</h1>
          <p className="text-xl text-white/95 max-w-3xl mx-auto">
            Join our network of trusted partners across the UK and international markets
          </p>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">Why Partner With Folalabi Farms?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We provide more than just products—we offer a complete partnership built on reliability, quality, and mutual growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:border-[#C8A951] transition-all hover:shadow-lg bg-white text-center">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-semibold text-[#333333] mb-3">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">Who We Partner With</h2>
            <p className="text-lg text-gray-600">
              We work with diverse partners across the food supply chain
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {partnerTypes.map((type, index) => (
              <Card key={index} className="border-0 shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-3">{type.title}</h3>
                  <p className="text-gray-700 mb-6">{type.description}</p>
                  <div className="space-y-2">
                    {type.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="text-[#1B5E20] flex-shrink-0" size={18} />
                        <span className="text-gray-600">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#333333] mb-4">How to Become a Partner</h2>
              <p className="text-lg text-gray-600">
                Simple steps to start a successful partnership with Folalabi Farms
              </p>
            </div>

            <div className="space-y-6">
              {[
                {
                  step: 1,
                  title: 'Initial Contact',
                  description: 'Reach out via our contact form or request a quote with your requirements'
                },
                {
                  step: 2,
                  title: 'Consultation',
                  description: 'Discuss your product needs, volumes, delivery schedules, and pricing'
                },
                {
                  step: 3,
                  title: 'Agreement',
                  description: 'Review terms, documentation requirements, and finalize partnership agreement'
                },
                {
                  step: 4,
                  title: 'First Order',
                  description: 'Place your first order and experience our professional service firsthand'
                },
                {
                  step: 5,
                  title: 'Ongoing Partnership',
                  description: 'Enjoy consistent supply, quality products, and dedicated account management'
                }
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-4 p-6 bg-[#F9F9F9] rounded-lg hover:shadow-md transition-all">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-xl">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#333333] mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Download Resources */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">Download Resources</h2>
            <p className="text-lg text-gray-600">
              Learn more about our products and services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {downloadResources.map((resource) => (
              <Card key={resource.id} className="border-0 shadow-lg bg-white hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#C8A951] to-[#B8994A] rounded-lg flex items-center justify-center mb-4">
                    <Download className="text-white" size={28} />
                  </div>
                  <h3 className="text-lg font-bold text-[#333333] mb-2">{resource.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{resource.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-gray-500">{resource.fileType}</span>
                    <span className="text-xs text-gray-500">{resource.fileSize}</span>
                  </div>
                  <Button className="w-full bg-[#1B5E20] hover:bg-[#2E7D32] text-white" onClick={() => alert('Download functionality will be implemented in backend')}>
                    Download {resource.fileType}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1B5E20] to-[#2E7D32] text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <Handshake className="w-20 h-20 mx-auto mb-6 text-[#C8A951]" />
            <h2 className="text-4xl font-bold mb-4">Ready to Partner With Us?</h2>
            <p className="text-xl mb-8 text-white/90">
              Let's discuss how we can support your African produce needs with reliable supply and professional service
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/quote">
                <Button size="lg" className="bg-[#C8A951] hover:bg-[#B8994A] text-white font-semibold px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all">
                  Request a Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-[#1B5E20] font-semibold px-10 py-6 text-lg transition-all">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partner;
